import { Button, CloseButton, Flex, Drawer, Accordion, Field, Dialog, DialogBody, DialogContent, DialogFooter, Portal } from '@chakra-ui/react';
import { FormInput, FormTextarea, toaster, useDrawer, Loading } from '@shared';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateSenderName, useDeleteSenderName, useGetSingleSenderName, useUpdateSenderName } from '../../api/hooks/useSenderNameSuperadmin';
import { Select } from 'chakra-react-select';
import { useGetAllAccounts } from '../../api/hooks/useAccountSuperadmin';

type SenderNameForm = {
	name: string;
	accountID: number;
	senderNameType: SenderNameSenderNameType;
	authorizationLevel: SenderNameAuthorizationLevel;
	companyName: string | null;
	companyWebsite: string | null;
	contactPersonName: string | null;
	contactEmail: string | null;
	primaryPurpose: SenderNamePurpose;
	secondaryPurpose: SenderNamePurpose | null;
	tertiaryPurpose: SenderNamePurpose | null;
	status?: SenderNameStatus;
	rejectReason?: string | null;
};

export default function SenderNameDrawer({ senderNameID }: { senderNameID?: number }) {
	const { t } = useTranslation();
	const { closeDrawer } = useDrawer();
	const queryClient = useQueryClient();
	const [isRemoveOpen, setIsRemoveOpen] = useState<boolean>(false);

	// Fetch existing sender name if updating
	const { data: senderName, isFetching } = useGetSingleSenderName(senderNameID as number, {
		isEnabled: !!senderNameID,
	});

	const createMutation = useCreateSenderName();
	const updateMutation = useUpdateSenderName();

	const methods = useForm<SenderNameForm>({
		defaultValues: senderNameID
			? { ...senderName }
			: {
					name: '',
					senderNameType: 'alphanumeric',
					authorizationLevel: 'authorized',
					companyName: '',
					companyWebsite: '',
					contactPersonName: '',
					contactEmail: '',
					primaryPurpose: 'trust',
					secondaryPurpose: null,
					tertiaryPurpose: null,
				},
	});

	const {
		handleSubmit,
		control,
		setValue,
		watch,
		reset,
		formState: { isDirty, dirtyFields },
	} = methods;

	useEffect(() => {
		if (senderName) {
			reset({ ...senderName });
		}
	}, [senderName, reset]);

	const status = useWatch({ control, name: 'status' });

	const onSubmit = async (values: SenderNameForm) => {
		if (!senderRegex.test(values.name)) {
			toaster.create({
				type: 'error',
				title: t('general.invalid_sender'),
			});
			return;
		}

		const payload: Partial<SenderNameForm> = {};
		Object.keys(dirtyFields).forEach(key => {
			// @ts-expect-error bää
			payload[key as keyof SenderNameForm] = values[key as keyof SenderNameForm];
		});

		if (senderNameID) {
			await updateMutation.mutateAsync({ senderNameID, ...payload });
			toaster.create({ type: 'success', title: t('general.updated') });
		} else {
			await createMutation.mutateAsync(values);
			toaster.create({ type: 'success', title: t('general.created') });
		}
		queryClient.invalidateQueries({ queryKey: ['senderNames'] });
		queryClient.invalidateQueries({ queryKey: ['senderName'] });

		closeDrawer('senderNameDrawer');
	};

	const { data: fetchedAccounts, isPending: isPendingAccounts } = useGetAllAccounts();
	function getAccountStatusTranslation(status: AccountStatus): string {
		switch (status) {
			case 'waitingForApproval':
				return t('general.waiting_for_approval');
			case 'rejected':
				return t('general.rejected');
			case 'approved':
				return t('general.approved');
			case 'locked':
				return t('general.locked');
			default:
				return t('general.unknown');
		}
	}

	const accountsCollection = fetchedAccounts?.data.accounts?.map(account => {
		let label = account.name;
		if (account.accountStatus === 'rejected' || account.accountStatus === 'locked') {
			label = `${label} - ${getAccountStatusTranslation(account.accountStatus).toUpperCase()}`;
		}
		return {
			label: label,
			value: account.accountID,
		};
	});

	const primaryPurpose = watch('primaryPurpose');
	const secondaryPurpose = watch('secondaryPurpose');
	const tertiaryPurpose = watch('tertiaryPurpose');
	const allPurposes = [
		{ value: 'transactional', label: t('general.transactional') },
		{ value: 'promotional', label: t('general.promotional') },
		{ value: 'trust', label: t('general.trust') },
	];

	useEffect(() => {
		if (secondaryPurpose === primaryPurpose) {
			setValue('secondaryPurpose', null, { shouldDirty: true });
		}

		if (tertiaryPurpose === primaryPurpose || tertiaryPurpose === secondaryPurpose) {
			setValue('tertiaryPurpose', null, { shouldDirty: true });
		}
	}, [primaryPurpose, secondaryPurpose, tertiaryPurpose, setValue]);

	const getAvailableOptions = (exclude: (string | null | undefined)[]) => allPurposes.filter(option => !exclude.includes(option.value));

	const senderRegex = /^(?:\+[0-9]{7,15}|[a-zA-Z0-9ÅÄÖåäö .-]{1,11})$/;

	return isFetching || isPendingAccounts ? (
		<Loading />
	) : (
		<FormProvider {...methods}>
			<form style={{ height: '100%', display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit(onSubmit)}>
				<Drawer.Header>
					<Drawer.Title wordBreak="break-word">{senderNameID ? t('general.edit_sender_name') : t('general.create_sender_name')}</Drawer.Title>
				</Drawer.Header>

				<Drawer.Body flex={1}>
					<Accordion.Root multiple collapsible defaultValue={['basicDetails', 'purposes']}>
						<Accordion.Item value="basicDetails">
							<Accordion.ItemTrigger cursor={'pointer'}>
								<Accordion.ItemIndicator />

								{t('general.basic_details')}
							</Accordion.ItemTrigger>
							<Accordion.ItemContent>
								<Accordion.ItemBody>
									<Flex flexDirection="column" gap={4}>
										<Controller
											name="status"
											control={control}
											render={({ field }) => (
												<Field.Root>
													<select
														style={{
															height: '40px',
															paddingInline: 'var(--tav-spacing-3)',
															borderRadius: 'var(--tav-radii-l2)',
															backgroundColor: 'white',
															border: 'var(--tav-colors-border) 1px solid',
															width: '100%',
														}}
														value={field.value || ''}
														onChange={e => field.onChange(e.target.value)}
													>
														<option value="" disabled hidden>
															{t('general.select')}
														</option>
														<option value="processingProspectum">{t('general.processing_prospectum')}</option>
														<option value="processingMyLink">{t('general.processing_my_link')}</option>
														<option value="accepted">{t('general.accepted')}</option>
														<option value="rejected">{t('general.rejected')}</option>
													</select>
												</Field.Root>
											)}
										/>
										{status === 'rejected' && (
											<FormTextarea
												isRequired
												name="rejectReason"
												label={t('general.reject_reason')}
												maxLength={500}
												registerOptions={{ validate: value => !!value?.trim() || t('general.required') }}
											/>
										)}

										<Field.Root>
											<Field.Label>{t('general.account')}</Field.Label>
											<Select
												required
												options={accountsCollection}
												value={
													watch('accountID')
														? { value: watch('accountID'), label: fetchedAccounts?.data.accounts.find(a => a.accountID === watch('accountID'))?.name }
														: null
												}
												onChange={e => {
													setValue('accountID', e?.value as number, {
														shouldDirty: true,
													});
												}}
											></Select>
										</Field.Root>
										<FormInput
											type={watch('senderNameType') === 'phone' ? 'tel' : 'text'}
											name="name"
											label={t('general.sender_name')}
											isRequired
											helperText={t('general.sender_name_case_sensitive')}
											maxLength={watch('senderNameType') === 'phone' ? 16 : 11}
										/>
										<Field.Root required={true}>
											<Field.Label>
												{t('general.sender_name_type')} <Field.RequiredIndicator />
											</Field.Label>
											<Controller
												rules={{ required: t('general.required') }}
												name="senderNameType"
												control={control}
												render={({ field, fieldState }) => (
													<Field.Root invalid={fieldState.invalid}>
														<select
															style={{
																height: '40px',
																paddingInline: 'var(--tav-spacing-3)',
																borderRadius: 'var(--tav-radii-l2)',
																backgroundColor: 'white',
																border: 'var(--tav-colors-border) 1px solid',
																width: '100%',
															}}
															value={field.value || ''} // default empty
															onChange={e => field.onChange(e.target.value)}
														>
															<option value="" disabled hidden>
																{t('general.select')}
															</option>
															<option value="alphanumeric">{t('general.alphanumeric')}</option>
															<option value="phone">{t('general.phone')}</option>
														</select>
														<Field.ErrorText id="errorText">{fieldState.error?.message}</Field.ErrorText>
													</Field.Root>
												)}
											/>
										</Field.Root>

										<Field.Root required={true}>
											<Field.Label>
												{t('general.authorization_level')} <Field.RequiredIndicator />
											</Field.Label>
											<Controller
												rules={{ required: t('general.required') }}
												name="authorizationLevel"
												control={control}
												render={({ field, fieldState }) => (
													<Field.Root invalid={fieldState.invalid}>
														<select
															style={{
																height: '40px',
																paddingInline: 'var(--tav-spacing-3)',
																borderRadius: 'var(--tav-radii-l2)',
																backgroundColor: 'white',
																border: 'var(--tav-colors-border) 1px solid',
																width: '100%',
															}}
															value={field.value || ''} // default empty
															onChange={e => field.onChange(e.target.value)}
														>
															<option value="" disabled hidden>
																{t('general.select')}
															</option>
															<option value="authorized">{t('general.authorized')}</option>
															<option value="registered">{t('general.registered')}</option>
														</select>
														<Field.ErrorText id="errorText">{fieldState.error?.message}</Field.ErrorText>
													</Field.Root>
												)}
											/>
										</Field.Root>

										<FormInput isRequired name="companyName" label={t('general.company_name')} maxLength={100} />
										<FormInput name="companyWebsite" label={t('general.company_website')} maxLength={200} />
										<FormInput isRequired name="contactPersonName" label={t('general.contact_person_name')} maxLength={100} />
										<FormInput isRequired name="contactEmail" label={t('general.contact_email')} maxLength={100} type="email" />
									</Flex>
								</Accordion.ItemBody>
							</Accordion.ItemContent>
						</Accordion.Item>

						<Accordion.Item value="purposes">
							<Accordion.ItemTrigger cursor={'pointer'}>
								<Accordion.ItemIndicator />
								{t('general.purposes')}
							</Accordion.ItemTrigger>
							<Accordion.ItemContent>
								<Accordion.ItemBody>
									<Flex flexDirection="column" gap={4}>
										<Field.Root>
											<Field.Label>{t('general.primary_purpose')}</Field.Label>

											<Controller
												name="primaryPurpose"
												control={control}
												rules={{ required: t('general.required') }}
												render={({ field, fieldState }) => (
													<Field.Root invalid={fieldState.invalid}>
														<select
															style={{
																height: '40px',
																paddingInline: 'var(--tav-spacing-3)',
																borderRadius: 'var(--tav-radii-l2)',
																backgroundColor: 'white',
																border: 'var(--tav-colors-border) 1px solid',
																width: '100%',
															}}
															value={field.value || ''}
															onChange={e => field.onChange(e.target.value)}
														>
															<option value="" disabled hidden>
																{t('general.select')}
															</option>
															<option value="transactional">{t('general.transactional')}</option>
															<option value="promotional">{t('general.promotional')}</option>
															<option value="trust">{t('general.trust')}</option>
														</select>
														<Field.ErrorText id="errorText">{fieldState.error?.message}</Field.ErrorText>
													</Field.Root>
												)}
											/>
										</Field.Root>

										<Field.Root>
											<Field.Label>{t('general.secondary_purpose')}</Field.Label>
											<Controller
												name="secondaryPurpose"
												control={control}
												render={({ field }) => (
													<Field.Root>
														<select
															style={{
																height: '40px',
																paddingInline: 'var(--tav-spacing-3)',
																borderRadius: 'var(--tav-radii-l2)',
																backgroundColor: 'white',
																border: 'var(--tav-colors-border) 1px solid',
																width: '100%',
															}}
															value={field.value || ''}
															onChange={e => field.onChange(e.target.value)}
														>
															<option value="" disabled hidden>
																{t('general.select')}
															</option>
															{getAvailableOptions([primaryPurpose]).map(option => (
																<option key={option.value} value={option.value}>
																	{option.label}
																</option>
															))}
														</select>
													</Field.Root>
												)}
											/>
										</Field.Root>

										<Field.Root>
											<Field.Label>{t('general.tertiary_purpose')}</Field.Label>
											<Controller
												name="tertiaryPurpose"
												control={control}
												render={({ field }) => (
													<Field.Root>
														<select
															style={{
																height: '40px',
																paddingInline: 'var(--tav-spacing-3)',
																borderRadius: 'var(--tav-radii-l2)',
																backgroundColor: 'white',
																border: 'var(--tav-colors-border) 1px solid',
																width: '100%',
															}}
															value={field.value || ''}
															onChange={e => field.onChange(e.target.value)}
														>
															<option value="" disabled hidden>
																{t('general.select')}
															</option>
															{getAvailableOptions([primaryPurpose, secondaryPurpose]).map(option => (
																<option key={option.value} value={option.value}>
																	{option.label}
																</option>
															))}
														</select>
													</Field.Root>
												)}
											/>
										</Field.Root>
									</Flex>
								</Accordion.ItemBody>
							</Accordion.ItemContent>
						</Accordion.Item>
					</Accordion.Root>
				</Drawer.Body>

				<Drawer.Footer>
					{senderNameID ? <RemoveSenderName senderNameID={senderNameID} isOpen={isRemoveOpen} setIsOpen={setIsRemoveOpen} /> : null}
					<Button id="removeSenderNameButton" marginRight={'auto'} onClick={() => setIsRemoveOpen(true)} variant="subtle">
						{t('general.remove_from_account')}
					</Button>
					<Button onClick={() => closeDrawer('senderNameDrawer')} variant="outline">
						{t('general.cancel')}
					</Button>
					<Button type="submit" disabled={!isDirty}>
						{t('general.save')}
					</Button>
				</Drawer.Footer>

				<Drawer.CloseTrigger asChild>
					<CloseButton size="sm" />
				</Drawer.CloseTrigger>
			</form>
		</FormProvider>
	);
}

function RemoveSenderName({ senderNameID, isOpen, setIsOpen }: { senderNameID: number; isOpen: boolean; setIsOpen: (value: boolean) => void }) {
	const { t } = useTranslation();
	const { closeDrawer } = useDrawer();
	const { mutate, isPending: isDeletingSenderName } = useDeleteSenderName();
	const queryClient = useQueryClient();

	function removeSenderName(): void {
		mutate(senderNameID, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.removed'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['senderNames'] });
				closeDrawer('senderNameDrawer');
			},
		});
	}

	return (
		<Dialog.Root open={isOpen} onOpenChange={e => setIsOpen(e.open)} placement="center">
			<Dialog.Backdrop />
			<Portal>
				<Dialog.Positioner>
					<DialogContent>
						<Dialog.Header>
							<Dialog.Title>{t('general.remove_sender_name')}</Dialog.Title>
						</Dialog.Header>
						<Dialog.CloseTrigger />
						<DialogBody>
							<p>{t('general.remove_sender_name')}?</p>
						</DialogBody>
						<DialogFooter>
							<Dialog.ActionTrigger asChild>
								<Button variant="outline">{t('general.cancel')}</Button>
							</Dialog.ActionTrigger>
							<Button id="confirmRemoveSenderNameButton" disabled={isDeletingSenderName} variant="subtle" onClick={removeSenderName}>
								{isDeletingSenderName ? t('general.please_wait') : t('general.remove')}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
}
